import { type Read, readAll, readOf } from '@/lib/readOf';
import { trpc } from '@/lib/trpc';

import { type ReactNode, createContext, useContext } from 'react';

import { useQuery } from '@tanstack/react-query';
import type { RouterOutputs } from '@teo/backend';

/** The three lists that the property windows read: properties, portfolios and ownerships. */
export interface PropertyLists {
  properties: RouterOutputs['properties']['getUserProperties'] | undefined;
  portfolios: RouterOutputs['portfolios']['getUserPortfolios'] | undefined;
  ownerships: RouterOutputs['ownership']['getUserOwnerships'] | undefined;
  /**
   * How far the reads behind the lists have got. A read that failed before it loaded gives
   * `failed`. A failed refresh over loaded lists keeps them `ready`.
   */
  read: Read<unknown>;
}

type WindowLists = RouterOutputs['onboarding']['windowLists'];

/**
 * Where the lists come from. `grants`: the lists of the signed-in user, which hold only what the
 * user's grants reach. `adminOrganization`: one organization, for a TEO admin in the onboarding
 * flow, who holds no grant on most organizations.
 */
type ListsSource = { kind: 'grants' } | { kind: 'adminOrganization'; lists: Read<WindowLists> };

const ListsSourceContext = createContext<ListsSource>({ kind: 'grants' });

/** The lists of a read that has data, also after a failed refresh. */
function loadedLists(read: Read<WindowLists>): WindowLists | undefined {
  return read.kind === 'ready' ? read.data : undefined;
}

/**
 * Gives the windows inside it the lists of one organization, as a TEO admin reads them. The
 * onboarding flow wraps today's windows in it, so the windows work with no grant made. The read
 * runs here, so a page outside the flow never reads it.
 */
export function AdminOrganizationLists({
  ownershipId,
  children,
}: {
  ownershipId: string;
  children: ReactNode;
}) {
  const lists = readOf(useQuery(trpc.onboarding.windowLists.queryOptions({ ownershipId })));
  return (
    <ListsSourceContext.Provider value={{ kind: 'adminOrganization', lists }}>
      {children}
    </ListsSourceContext.Provider>
  );
}

/** The three lists, from the source that the nearest provider sets. */
export function usePropertyLists(): PropertyLists {
  const source = useContext(ListsSourceContext);
  const ownLists = source.kind === 'grants';
  const properties = useQuery({
    ...trpc.properties.getUserProperties.queryOptions(),
    enabled: ownLists,
  });
  const portfolios = useQuery({
    ...trpc.portfolios.getUserPortfolios.queryOptions(),
    enabled: ownLists,
  });
  const ownerships = useQuery({
    ...trpc.ownership.getUserOwnerships.queryOptions(),
    enabled: ownLists,
  });

  switch (source.kind) {
    case 'grants':
      return {
        properties: properties.data,
        portfolios: portfolios.data,
        ownerships: ownerships.data,
        read: readAll(readOf(properties), readOf(portfolios), readOf(ownerships)),
      };
    case 'adminOrganization': {
      const lists = loadedLists(source.lists);
      return {
        properties: lists?.properties,
        portfolios: lists?.portfolios,
        ownerships: lists?.ownerships,
        read: source.lists,
      };
    }
    default: {
      const _exhaustive: never = source;
      throw new Error(`Unhandled lists source: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

/**
 * The properties list alone, from the source that the nearest provider sets. A reader that needs
 * only the stored property, such as a review step, uses this and starts no other read.
 */
export function useListedProperties(): Pick<PropertyLists, 'properties'> {
  const source = useContext(ListsSourceContext);
  const properties = useQuery({
    ...trpc.properties.getUserProperties.queryOptions(),
    enabled: source.kind === 'grants',
  });
  switch (source.kind) {
    case 'grants':
      return { properties: properties.data };
    case 'adminOrganization':
      return { properties: loadedLists(source.lists)?.properties };
    default: {
      const _exhaustive: never = source;
      throw new Error(`Unhandled lists source: ${JSON.stringify(_exhaustive)}`);
    }
  }
}
